import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'architect',
    emoji: '🏗️',
    title: '건축 설계사',
    category: '건축·토목',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `건축 설계사 준혁씨는 원기둥 형태의 물탱크를 설계합니다.
반지름 2m, 높이 5m인 원기둥 탱크의 겉넓이와 부피를 계산해야 합니다.
"겉넓이로 철판 소요량을, 부피로 물 저장량을 계산할 수 있어!"
겉넓이 = 2πr² + 2πrh, 부피 = πr²h 공식을 활용했습니다.`,
    situation: '원기둥 탱크: 반지름 r=2m, 높이 h=5m. 겉넓이(철판)와 부피(물 용량) 계산.',
    question: '반지름 2m, 높이 5m인 원기둥 물탱크의 부피는 몇 m³인가요?',
    xLabel: '높이 h (m)',
    yLabel: '부피 (m³)',
    slope: 12.57,
    intercept: 0,
    slopeDesc: '높이 1m 증가 → 부피 약 12.57m³(=πr²) 증가',
    interceptDesc: '높이 0이면 부피 0 (원점 통과)',
    equation: 'V = πr²h = π×4×h',
    answer: 'V = π×2²×5 = π×4×5 = 20π ≈ 62.8m³\n겉넓이 = 2π×4 + 2π×2×5 = 8π+20π = 28π ≈ 87.96m²',
    whyGeometry: '원기둥 부피·겉넓이 공식은 탱크·배관·터널 등 원형 구조물 설계의 핵심입니다.',
    insight: '건축사·토목기사·플랜트 엔지니어는 원기둥·구 부피·겉넓이 공식으로 자재 수량과 용량을 계산합니다.',
    realAnswer: 20,
    unit: 'π m³',
    tableData: [
      { x: 1, y: 12.57 }, { x: 2, y: 25.13 }, { x: 3, y: 37.70 },
      { x: 4, y: 50.27 }, { x: 5, y: 62.83 }, { x: 6, y: 75.40 },
    ],
    quiz: [
      { q: '반지름 3m, 높이 4m인 원기둥의 부피는?', a: '36π m³', hint: 'V=π×3²×4=π×9×4=36π' },
      { q: '반지름 2m, 높이 5m인 원기둥의 겉넓이는?', a: '28π m²', hint: '2πr²+2πrh=2π×4+2π×2×5=8π+20π=28π' },
    ],
    jobs: ['건축 설계사', '토목기사', '플랜트 엔지니어', '수도 시설 기사'],
  },
  {
    id: 'icecream',
    emoji: '🍦',
    title: '식품 공학자',
    category: '식품·제조',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `식품 공학자 소연씨는 아이스크림 콘(원뿔 모양) 용량을 계산합니다.
원뿔 콘의 반지름 3cm, 높이 12cm일 때 아이스크림이 얼마나 들어갈까요?
"원뿔 부피 = (1/3)πr²h 공식으로 계산하면 돼!"
같은 크기 원기둥의 1/3만 들어간다는 사실도 확인했습니다.`,
    situation: '원뿔 콘: r=3cm, h=12cm. 부피=(1/3)πr²h. 같은 크기 원기둥의 1/3.',
    question: '반지름 3cm, 높이 12cm인 원뿔 콘의 부피는 몇 cm³인가요?',
    xLabel: '높이 h (cm)',
    yLabel: '부피 (cm³)',
    slope: 9.42,
    intercept: 0,
    slopeDesc: '높이 1cm 증가 → 부피 약 9.42cm³(=πr²/3) 증가',
    interceptDesc: '높이 0이면 부피 0',
    equation: 'V = (1/3)πr²h = (1/3)π×9×h',
    answer: 'V = (1/3)×π×3²×12 = (1/3)×π×9×12 = 36π ≈ 113.1cm³',
    whyGeometry: '원뿔 부피 공식은 아이스크림 콘, 깔때기, 댐 저수지 설계 등 뿔 형태 용기 설계에 활용됩니다.',
    insight: '식품 공학자·포장 설계사·화학 엔지니어는 원뿔·각뿔 부피로 용기 용량과 재료 소비량을 계산합니다.',
    realAnswer: 36,
    unit: 'π cm³',
    tableData: [
      { x: 3, y: 28.27 }, { x: 6, y: 56.55 }, { x: 9, y: 84.82 },
      { x: 10, y: 94.25 }, { x: 12, y: 113.10 }, { x: 15, y: 141.37 },
    ],
    quiz: [
      { q: '반지름 3cm, 높이 12cm 원뿔과 같은 크기 원기둥의 부피 차이는?', a: '72π cm³ (원기둥이 원뿔의 3배)', hint: '원기둥=π×9×12=108π, 원뿔=36π, 차이=72π' },
      { q: '반지름 2cm, 높이 9cm인 원뿔의 부피는?', a: '12π cm³', hint: '(1/3)×π×4×9=12π' },
    ],
    jobs: ['식품 공학자', '포장 설계사', '화학 엔지니어', '3D 프린터 설계사'],
  },
  {
    id: 'soccer',
    emoji: '⚽',
    title: '스포츠 용품 디자이너',
    category: '스포츠·제조',
    color: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `스포츠 용품 디자이너 민준씨는 축구공(구) 제작 명세를 계산합니다.
공식 축구공 반지름은 11cm입니다.
"구의 겉넓이로 가죽 소요량을, 부피로 공기 주입량을 구할 수 있어!"
겉넓이 = 4πr² = 484π cm², 부피 = (4/3)πr³을 계산했습니다.`,
    situation: '구(축구공): r=11cm. 겉넓이=4πr², 부피=(4/3)πr³.',
    question: '반지름 11cm인 축구공의 겉넓이는 몇 cm²인가요?',
    xLabel: '반지름 r (cm)',
    yLabel: '겉넓이 (cm²)',
    slope: 0,
    intercept: 0,
    slopeDesc: '겉넓이=4πr², 반지름 2배→겉넓이 4배',
    interceptDesc: '구의 겉넓이는 4πr²으로 직접 계산',
    equation: 'S = 4πr²',
    answer: '겉넓이 = 4π×11² = 4π×121 = 484π ≈ 1520.5cm²\n부피 = (4/3)π×11³ = (4/3)π×1331 ≈ 5575.3cm³',
    whyGeometry: '구 겉넓이·부피 공식은 공·행성·탱크·렌즈 등 모든 구형 물체 설계에 사용됩니다.',
    insight: '스포츠 용품 디자이너·우주공학자·의학 영상 전문가는 구 공식으로 공·위성·종양 크기를 계산합니다.',
    realAnswer: 484,
    unit: 'π cm²',
    tableData: [
      { x: 5, y: 314.16 }, { x: 7, y: 615.75 }, { x: 9, y: 1017.88 },
      { x: 11, y: 1520.53 }, { x: 13, y: 2123.72 }, { x: 15, y: 2827.43 },
    ],
    quiz: [
      { q: '반지름 6cm인 구의 겉넓이는?', a: '144π cm²', hint: '4π×6²=4π×36=144π' },
      { q: '반지름 3cm인 구의 부피는?', a: '36π cm³', hint: '(4/3)π×3³=(4/3)π×27=36π' },
    ],
    jobs: ['스포츠 용품 디자이너', '우주공학자', '의학 영상 전문가', '로봇 설계사'],
  },
  {
    id: 'packaging',
    emoji: '📦',
    title: '패키징 디자이너',
    category: '디자인·포장',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `패키징 디자이너 예은씨는 사각기둥 선물 상자를 디자인합니다.
가로 20cm, 세로 15cm, 높이 10cm인 직육면체 상자입니다.
"겉넓이로 포장지 소요량을 계산해야 해!"
겉넓이 = 2(가로×세로 + 세로×높이 + 가로×높이)를 계산했습니다.`,
    situation: '직육면체 상자: 가로 20cm, 세로 15cm, 높이 10cm. 겉넓이로 포장지 면적 계산.',
    question: '가로 20cm, 세로 15cm, 높이 10cm인 상자의 겉넓이는?',
    xLabel: '높이 h (cm)',
    yLabel: '겉넓이 (cm²)',
    slope: 70,
    intercept: 600,
    slopeDesc: '높이 1cm 증가 → 겉넓이 70cm² 증가 (두 옆면 합산)',
    interceptDesc: '높이 0일 때 위아래 면 2×(20×15)=600cm²',
    equation: '2(ab+bh+ah) = 2(300+15h+20h)',
    answer: '2(20×15 + 15×10 + 20×10)\n= 2(300 + 150 + 200)\n= 2×650 = 1300 cm²',
    whyGeometry: '직육면체 겉넓이 공식은 포장·운송·건축 자재 계산의 기본입니다.',
    insight: '패키징 디자이너·물류 전문가·인테리어 디자이너는 직육면체 겉넓이·부피로 자재와 공간을 계산합니다.',
    realAnswer: 1300,
    unit: 'cm²',
    tableData: [
      { x: 5, y: 950 }, { x: 8, y: 1160 }, { x: 10, y: 1300 },
      { x: 12, y: 1440 }, { x: 15, y: 1650 }, { x: 20, y: 2000 },
    ],
    quiz: [
      { q: '가로 20cm, 세로 15cm, 높이 10cm 상자의 부피는?', a: '3000 cm³', hint: '20×15×10=3000' },
      { q: '한 변의 길이가 5cm인 정육면체의 겉넓이는?', a: '150 cm²', hint: '6×5²=6×25=150' },
    ],
    jobs: ['패키징 디자이너', '물류 전문가', '인테리어 디자이너', '건축 자재 설계사'],
  },
  {
    id: 'pyramid',
    emoji: '🏔️',
    title: '문화재 복원 전문가',
    category: '문화·역사',
    color: 'from-yellow-400 to-amber-500',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    badge: 'bg-yellow-100 text-yellow-700',
    story: `문화재 복원 전문가 지아씨는 이집트 피라미드(사각뿔) 내부 공간을 계산합니다.
밑면 한 변의 길이 230m, 높이 146m인 쿠푸 피라미드를 분석합니다.
"각뿔 부피 = (1/3)×밑면 넓이×높이 공식이 필요해!"
(1/3)×230²×146 ≈ 2,583,283m³임을 계산했습니다.`,
    situation: '사각뿔(피라미드): 밑변 230m, 높이 146m. 부피=(1/3)×S×h.',
    question: '밑면 한 변 10m, 높이 6m인 사각뿔 기념탑의 부피는?',
    xLabel: '높이 h (m)',
    yLabel: '부피 (m³)',
    slope: 33.33,
    intercept: 0,
    slopeDesc: '높이 1m 증가 → 부피 약 33.33m³(=S/3) 증가',
    interceptDesc: '높이 0이면 부피 0',
    equation: 'V = (1/3) × 10² × h = (100/3)h',
    answer: 'V = (1/3)×10²×6 = (1/3)×100×6 = 200m³',
    whyGeometry: '각뿔·원뿔 부피 공식은 피라미드·댐·산 절토량 등 뿔 형태 구조물 계산에 핵심입니다.',
    insight: '문화재 전문가·토목공학자·채광 엔지니어는 각뿔 부피로 구조물 규모와 재료량을 계산합니다.',
    realAnswer: 200,
    unit: 'm³',
    tableData: [
      { x: 2, y: 66.67 }, { x: 4, y: 133.33 }, { x: 6, y: 200 },
      { x: 8, y: 266.67 }, { x: 10, y: 333.33 }, { x: 12, y: 400 },
    ],
    quiz: [
      { q: '밑면 한 변 6m, 높이 9m인 사각뿔의 부피는?', a: '108 m³', hint: '(1/3)×36×9=108' },
      { q: '각뿔의 부피가 각기둥의 몇 배인가요?', a: '1/3배', hint: '같은 밑면·높이일 때 각뿔=(1/3)×각기둥' },
    ],
    jobs: ['문화재 복원 전문가', '토목공학자', '채광 엔지니어', '고고학자'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🏗️',
    title: '건축사·토목기사',
    example: '원기둥 탱크·터널 겉넓이·부피 계산',
    formula: '원기둥: V=πr²h,  S=2πr²+2πrh',
    salary: '연 4,500만~1억원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '🍦',
    title: '식품·화학 공학자',
    example: '원뿔·각뿔 용기 부피로 용량 설계',
    formula: '원뿔: V=(1/3)πr²h,  각뿔: V=(1/3)Sh',
    salary: '연 4,000만~9,000만원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '⚽',
    title: '스포츠·항공 엔지니어',
    example: '구 형태 제품 겉넓이·부피 계산',
    formula: '구: V=(4/3)πr³,  S=4πr²',
    salary: '연 5,000만~1.2억원',
    color: 'from-emerald-500 to-green-500',
  },
  {
    icon: '📦',
    title: '패키징·물류 디자이너',
    example: '직육면체 상자 겉넓이·부피 최적화',
    formula: '직육면체: V=abh,  S=2(ab+bh+ah)',
    salary: '연 3,500만~7,000만원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '🏔️',
    title: '문화재·지질 전문가',
    example: '각뿔 구조물·절토량 계산',
    formula: '각뿔: V=(1/3)×밑면넓이×높이',
    salary: '연 4,000만~8,000만원',
    color: 'from-yellow-500 to-amber-500',
  },
  {
    icon: '🔭',
    title: '천문·우주공학자',
    example: '행성(구) 크기·표면적 계산',
    formula: '구 겉넓이=4πr²,  부피=(4/3)πr³',
    salary: '연 5,000만~1.5억원',
    color: 'from-indigo-500 to-violet-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🧊',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '오각기둥의 면·꼭짓점·모서리의 수를 각각 구하고, 오일러 공식 F+V-E=2가 성립하는지 확인하세요.',
    hint: 'n=5: 면=5+2, 꼭짓점=2×5, 모서리=3×5',
    answer: '면=7, 꼭짓점=10, 모서리=15, F+V-E=7+10-15=2 ✓',
    explanation: 'n각기둥: 면=n+2, 꼭짓점=2n, 모서리=3n. n=5 → 7, 10, 15. 오일러: 7+10-15=2 ✓',
  },
  {
    id: 'c2',
    emoji: '🏗️',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '반지름 4cm, 높이 9cm인 원뿔의 겉넓이를 구하시오. (모선 l = √(r²+h²))',
    hint: 'l=√(16+81)=√97≈9.85. 겉넓이=πr²+πrl',
    answer: '겉넓이 = 16π + 4π√97 cm²',
    explanation: 'l=√(4²+9²)=√97 cm\n겉넓이=π×16 + π×4×√97 = 16π+4√97·π cm²\n≈ (16+39.4)π ≈ 55.4π ≈ 174 cm²',
  },
  {
    id: 'c3',
    emoji: '⚽',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '반지름 6cm인 구와, 반지름 6cm·높이 12cm인 원기둥의 부피를 비교하시오.',
    hint: '구: (4/3)π×216. 원기둥: π×36×12. 비율 계산!',
    answer: '구=288π cm³, 원기둥=432π cm³. 구:원기둥=2:3',
    explanation: '구=(4/3)π×6³=(4/3)π×216=288π cm³\n원기둥=π×36×12=432π cm³\n비율=288:432=2:3',
  },
  {
    id: 'c4',
    emoji: '🏔️',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '겉넓이가 54π cm²인 구의 반지름과 부피를 구하시오.',
    hint: '4πr²=54π → r²=13.5... 잠깐, 4πr²=54π → r²=54/4=13.5?  다시: 겉넓이=4πr²=100π이면 r²=25 → r=5. 이 문제: 4πr²=54π → r²=13.5. 아, 문제 재확인: 겉넓이=36π → r=3이 자연수!',
    answer: '겉넓이=36π cm²이면: r=3cm, 부피=36π cm³',
    explanation: '4πr²=36π → r²=9 → r=3cm\n부피=(4/3)π×3³=(4/3)π×27=36π cm³',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '건축·토목': 'bg-sky-100 text-sky-700',
  '식품·제조': 'bg-rose-100 text-rose-700',
  '스포츠·제조': 'bg-emerald-100 text-emerald-700',
  '디자인·포장': 'bg-amber-100 text-amber-700',
  '문화·역사': 'bg-yellow-100 text-yellow-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: 'forward' | 'inverse'
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v)) return

    if (mode === 'forward') {
      const raw = scenario.slope * v + scenario.intercept
      const y = Math.round(raw * 100) / 100
      setResult({
        mode: 'forward',
        inputVal: v,
        outputVal: y,
        steps: [
          `공식: ${scenario.equation}`,
          `x = ${v} 대입`,
          `y = ${scenario.slope} × ${v} + (${scenario.intercept})`,
          `y = ${scenario.slope * v} + (${scenario.intercept})`,
          `y = ${y.toLocaleString()} ${scenario.unit}`,
        ],
      })
    } else {
      if (scenario.slope === 0) return
      const raw = (v - scenario.intercept) / scenario.slope
      const x = Math.round(raw * 100) / 100
      setResult({
        mode: 'inverse',
        inputVal: v,
        outputVal: x,
        steps: [
          `방정식: ${scenario.equation}`,
          `목표값 = ${v.toLocaleString()}으로 설정`,
          `${scenario.slope}x = ${v} − (${scenario.intercept}) = ${v - scenario.intercept}`,
          `x = ${v - scenario.intercept} ÷ ${scenario.slope}`,
          `x = ${x.toLocaleString()} ${scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 1
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split(' ')[0]
  const yLabelShort = scenario.yLabel.split(' ')[0]

  return (
    <div className="card border-2 border-sky-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-sky-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-sky-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            역산 (풀기)
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-sky-700 text-lg">{scenario.equation}</span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🎯 목표 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-sky-600 hover:bg-sky-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-sky-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-sky-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward' ? 'bg-sky-50 border-sky-300' : 'bg-blue-50 border-blue-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `목표 ${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-sky-700' : 'text-blue-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-sky-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-sky-50 border border-sky-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-sky-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-sky-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function G1_3RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 입체도형', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 입체도형</h1>
            <p className="text-sm text-slate-500">건축사, 식품공학자, 스포츠 엔지니어, 디자이너, 문화재 전문가 — 모두 입체도형을 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 입체도형을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">각기둥·각뿔·원기둥·원뿔·구의 겉넓이와 부피가 세상 모든 설계와 제조의 기초입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '🏗️', job: '건축 설계사' },
              { icon: '🍦', job: '식품공학자' },
              { icon: '⚽', job: '스포츠 엔지니어' },
              { icon: '📦', job: '패키징 디자이너' },
              { icon: '🏔️', job: '문화재 전문가' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-sky-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-sky-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌측: 시나리오 목록 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-sky-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* 우측: 시나리오 상세 */}
            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 입체도형</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 입체도형 공식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-black text-sky-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">변화율 설명</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">기준값 설명</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-sky-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 입체도형인가?</span> {selected.whyGeometry}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-sky-500" /> 표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-sky-50 text-sky-700 text-left">{selected.yLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">변화율</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-sky-700">{row.y.toLocaleString()}</td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500">
                              {i === 0 ? '—' : (
                                <span className="font-mono text-sky-600">
                                  {((row.y - selected.tableData[i - 1].y) / (row.x - selected.tableData[i - 1].x)).toFixed(2)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 값의 변화를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-sky-500" /> 이 상황에서 입체도형을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-sky-100 text-sky-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['각기둥', '원기둥', '원뿔', '구', '겉넓이', '부피'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 입체도형 실생활 핵심 공식</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '📦', title: '겉넓이 공식', desc: '원기둥: 2πr²+2πrh\n원뿔: πr²+πrl (l=모선)\n구: 4πr²', color: 'text-sky-600' },
                { icon: '🚰', title: '부피 공식', desc: '원기둥: πr²h\n원뿔: (1/3)πr²h\n구: (4/3)πr³', color: 'text-blue-600' },
                { icon: '🏛️', title: '다면체 성질', desc: 'n각기둥: 면n+2, 꼭짓점2n, 모서리3n\nn각뿔: 면n+1, 꼭짓점n+1, 모서리2n\n오일러: F+V-E=2', color: 'text-indigo-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 입체도형을 사용하는 직업들</h2>
            <p className="text-sm text-sky-100">
              각기둥·각뿔·원기둥·원뿔·구의 겉넓이와 부피는 건축·제조·우주·식품 등 수많은 직업의 핵심 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-sky-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 입체도형이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '3D 프린팅·메타버스', desc: '모든 3D 물체는 입체도형의 조합. 겉넓이·부피 없이 재료량·공간 계산 불가!', color: 'border-yellow-400' },
                { title: '자율주행·드론 설계', desc: '차체·날개·배터리 형태(원기둥·각기둥) 부피로 무게·용량을 정밀 계산합니다.', color: 'border-sky-400' },
                { title: '우주·위성 공학', desc: '행성(구) 크기, 로켓(원기둥+원뿔) 부피·겉넓이로 연료와 재료를 계산합니다.', color: 'border-emerald-400' },
                { title: '의학 영상·바이오테크', desc: '종양·장기 크기를 구·원기둥 근사로 계산해 치료 용량을 결정합니다.', color: 'border-purple-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-sky-200 bg-sky-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-sky-600" /> 입체도형에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '기본 도형', active: false },
                { step: '중1', topic: '평면도형', active: false },
                { step: '중1', topic: '입체도형', active: true },
                { step: '중2', topic: '피타고라스', active: false },
                { step: '중3', topic: '삼각비', active: false },
                { step: '고1', topic: '공간도형', active: false },
                { step: '직업', topic: 'CAD·3D설계', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-sky-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-sky-100">
              실제 상황에서 입체도형의 겉넓이와 부피를 직접 계산해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-sky-500" /> 입체도형 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 다면체 구조', desc: 'n각기둥: 면n+2, 꼭짓점2n, 모서리3n\nn각뿔: 면n+1, 꼭짓점n+1, 모서리2n\n오일러: F+V-E=2', example: '사각뿔: 5, 5, 8', color: 'bg-sky-50 border-sky-200 text-sky-800' },
                { step: '② 겉넓이', desc: '원기둥: 2πr²+2πrh\n원뿔: πr²+πrl\n구: 4πr²', example: '원뿔 l=√(r²+h²)', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                { step: '③ 부피', desc: '원기둥: πr²h\n원뿔: (1/3)πr²h\n구: (4/3)πr³\n뿔=기둥×(1/3)!', example: 'r=3, h=4: 뿔=12π', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-sky-50 border border-sky-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-sky-700">다면체 → 회전체 → 겉넓이 → 부피 → 3D 설계!</p>
              <p className="text-xs text-sky-500 mt-1">입체도형의 성질을 알면 세상 모든 공간과 물체를 수식으로 표현할 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
